import { KeyboardIcon, MusicUploadSelectIcon } from '@/components/Icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/utils';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { PastPromptItem, usePastePrompt } from '../../hooks/usePastePrompt';

interface PastPromptSelectProps {
  handleClickPastPrompt: (prompt: string) => void;
}

const PastPromptSelectItem = ({
  promptItem,
  isSelected,
  onSelect,
}: {
  promptItem: PastPromptItem;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <div
    key={promptItem.id}
    className={cn('flex items-center justify-between hover:bg-gray-800 p-2 rounded cursor-pointer relative group transition duration-200', {
      ['bg-gray-800']: isSelected,
    })}
    onClick={onSelect}
  >
    <div className="flex justify-between items-center w-full">
      <span className="inline-block overflow-hidden text-ellipsis whitespace-nowrap max-w-[240px] text-white text-sm">
        {promptItem.prompt}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-white/50 whitespace-nowrap">{dayjs(promptItem.created_at).format('HH:mm DD/MM/YY')}</span>
      </div>
    </div>
  </div>
);

const PastPromptSelect: React.FC<PastPromptSelectProps> = ({ handleClickPastPrompt }) => {
  const [isOpenHummingAudioList, setIsOpenHummingAudioList] = useState(false);

  const { pastePrompt } = usePastePrompt();

  const [selectedPromptItem, setSelectedPromptItem] = useState<PastPromptItem | null>(null);

  const handleSelect = (promptItem: PastPromptItem) => {
    setSelectedPromptItem(promptItem);
    setIsOpenHummingAudioList(false);
    handleClickPastPrompt(promptItem.prompt);
  };

  return (
    <div className="px-2 py-2">
      <Popover open={isOpenHummingAudioList} onOpenChange={setIsOpenHummingAudioList}>
        <PopoverTrigger asChild>
          <div
            className="flex justify-between cursor-pointer hover:bg-gray-800 p-2 rounded transition duration-200"
            onClick={() => setIsOpenHummingAudioList(true)}
          >
            <span className="flex items-center w-[calc(100%-30px)]">
              <KeyboardIcon className="w-6 h-6 mr-2" />
              <span className="truncate max-w-[calc(100%-30px)] text-white">{selectedPromptItem?.prompt ?? 'Previous Prompts'}</span>
            </span>
            <span className="flex items-center">
              <MusicUploadSelectIcon className="w-5 h-5 ml-2" />
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="bg-black border-0 p-2 w-[19.5rem] lg:w-[28rem]">
          <ScrollArea className="h-[16rem] w-full">
            {!(pastePrompt ?? []).length && <div className="text-white/50 text-center min-h-6 pt-24">No Previous Prompts</div>}
            {(pastePrompt ?? []).map((promptItem) => (
              <PastPromptSelectItem
                key={promptItem.id}
                promptItem={promptItem}
                isSelected={false}
                onSelect={() => handleSelect(promptItem)}
              />
            ))}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PastPromptSelect;
